import {
  BlockTitle,
  Button,
  f7,
  Icon,
  List,
  ListItem,
  SwipeoutActions,
  SwipeoutButton,
} from "framework7-react";
import { useEffect, useState } from "react";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useDeleteEntry } from "../hooks/use-delete-entry";
import {
  isIncome,
  isPayment,
  noteOf,
  paymentSubtitle,
} from "../utils/expense";
import { isSearchActive, searchExpenses } from "../utils/expense-search";
import {
  describeReceivers,
  filterByTags,
  groupExpensesByDay,
  UNTAGGED,
} from "../utils/expense-list";
import { countLabel, formatCents } from "../utils/format";
import { PENDING_TEXT } from "../utils/outbox";
import ExpenseSearch from "./expense-search";
import TagFilter from "./tags/tag-filter";

const SEARCH_DEBOUNCE_MS = 200;

const RowTags = ({ tags }) => (
  <div className="expense-row-tags">
    {tags.map((tag) => (
      <span
        key={tag.id}
        className="expense-row-tag"
        style={tag.color ? { "--tag-color": tag.color } : undefined}
      >
        {tag.name}
      </span>
    ))}
  </div>
);

const ROW_ICONS = {
  payment: { ios: "f7:arrow_right_arrow_left", md: "material:swap_horiz" },
  income: { ios: "f7:arrow_down_circle", md: "material:savings" },
  expense: { ios: "f7:cart", md: "material:shopping_cart" },
};

const ExpenseRow = ({ expense, participants, onOpen, onDelete }) => {
  const payment = isPayment(expense);
  const income = isIncome(expense);
  const tags = Array.isArray(expense.tags) ? expense.tags : [];
  const amount = formatCents(expense.amount_in_decimals);
  return (
    <ListItem
      link
      swipeout
      className={[
        "expense-row",
        payment && "payment-row",
        income && "income-row",
        expense.recurring_id != null && "expense-recurring",
        expense.pending && "expense-pending",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onOpen(expense)}
      title={payment ? describeReceivers(expense, participants) : expense.name}
      subtitle={
        payment
          ? paymentSubtitle(expense)
          : describeReceivers(expense, participants)
      }
      text={expense.pending ? PENDING_TEXT : noteOf(expense)}
      after={income ? `+${amount}` : amount}
    >
      <Icon
        slot="media"
        {...ROW_ICONS[payment ? "payment" : income ? "income" : "expense"]}
      />
      {tags.length > 0 && <RowTags slot="inner" tags={tags} />}
      <SwipeoutActions right>
        <SwipeoutButton color="red" onClick={() => onDelete(expense)}>
          Löschen
        </SwipeoutButton>
      </SwipeoutActions>
    </ListItem>
  );
};

const OPENED_SWIPEOUT = ".expense-list .swipeout-opened";

// Removing the row while F7 animates it leaves its swipeouts locked for 500 ms.
const afterSwipeoutClosed = (callback) => {
  if (!document.querySelector(OPENED_SWIPEOUT)) {
    callback();
    return;
  }
  f7.swipeout.close(OPENED_SWIPEOUT, callback);
};

const NO_TAGS = [];

const ExpenseOverview = ({
  group: { id: groupId, expenses = [], participants = [], tags = NO_TAGS },
  onOpenExpense,
  onOpenPayment,
  onManageTags,
}) => {
  const { removeWithoutConfirm } = useDeleteEntry();
  const [filter, setFilter] = useState([]);
  const [query, setQuery] = useState("");
  const searchQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const groupTags = Array.isArray(tags) ? tags : NO_TAGS;

  useEffect(() => {
    setFilter([]);
    setQuery("");
  }, [groupId]);

  const activeFilter = filter.filter(
    (id) => id === UNTAGGED || groupTags.some((tag) => `${tag.id}` === id),
  );

  const deleteRow = (expense) =>
    afterSwipeoutClosed(() => removeWithoutConfirm(expense, participants));

  const open = (expense) =>
    isPayment(expense) ? onOpenPayment(expense) : onOpenExpense(expense);

  if (expenses.length === 0) return "Noch keine Ausgaben";

  const searching = isSearchActive(searchQuery);
  const shown = searchExpenses(
    filterByTags(expenses, activeFilter),
    searchQuery,
    participants,
  );
  const days = groupExpensesByDay(shown, new Date());
  const filtering = searching || activeFilter.length > 0;

  return (
    // swiper-no-swiping: a row swipe must not switch the tab.
    <div className="expense-list swiper-no-swiping">
      <ExpenseSearch value={query} onChange={setQuery} />
      {groupTags.length > 0 && (
        <TagFilter
          tags={groupTags}
          filter={activeFilter}
          onChange={setFilter}
          onManage={onManageTags}
        />
      )}
      {filtering && shown.length > 0 && (
        <p className="expense-result-count">
          {shown.length} von{" "}
          {countLabel(expenses.length, "Eintrag", "Einträgen")}
        </p>
      )}
      {shown.length === 0 && searching && (
        <div className="expense-search-empty">
          <p>Nichts gefunden für „{searchQuery.trim()}“</p>
          <Button
            className="expense-search-reset"
            small
            tonal
            onClick={() => setQuery("")}
          >
            Suche zurücksetzen
          </Button>
        </div>
      )}
      {shown.length === 0 && !searching && (
        <p className="expense-filter-empty">
          Keine Ausgaben mit diesen Tags.
        </p>
      )}
      {days.map((day) => (
        <div className="expense-day" key={day.key} data-day={day.key}>
          <BlockTitle className="expense-day-title">{day.label}</BlockTitle>
          <List dividers mediaList outline strong>
            {day.expenses.map((expense) => (
              <ExpenseRow
                key={expense.id}
                expense={expense}
                participants={participants}
                onOpen={open}
                onDelete={deleteRow}
              />
            ))}
          </List>
        </div>
      ))}
    </div>
  );
};

export default ExpenseOverview;
